import { AlertTriangle, FileSpreadsheet, Loader2 } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  WricefClientReportOptions,
  WricefClientReportPreview,
  WricefClientReportScope,
} from "@/api/wricef-catalog";
import { DECISION_BAR } from "@/components/projects/diagnostic/acts/environment/WricefCard";
import {
  DECISION_ORDER,
  type DecisionOrUndecided,
  UNDECIDED,
} from "@/components/projects/diagnostic/data/wricefDecisions";
import { makeDecisionMeta } from "@/components/projects/diagnostic/diagnosticMeta";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCurrentUser } from "@/hooks/useAuth";
import {
  useDownloadWricefClientReport,
  useWricefClientReportPreview,
} from "@/hooks/useWricefClientReport";
import { detailCode } from "@/lib/api-error";
import { tp } from "@/lib/plural";
import { hasMinRole } from "@/lib/roles";
import { cn } from "@/lib/utils";
import type { WricefCatalogQuery } from "@/types/wricef";

/** O "sem decisão" na contagem da API, que é o `UNDECIDED` da tela. */
const API_UNDECIDED = "undecided";

/**
 * # e Objeto ficam parados quando a prévia rola para o lado, como as duas
 * colunas congeladas da aba "WRICEF" no Excel. A # tem largura fixa porque a
 * segunda coluna parada começa onde ela termina.
 */
const STICKY_COLUMNS: Record<string, string> = {
  row_number: "sticky left-0 z-10 w-10 min-w-[2.5rem]",
  obj_name: "sticky left-10 z-10 border-r",
};

/**
 * "Relatório para o cliente" no Catálogo WRICEF (#1620, EF-ARCH-002).
 *
 * O consultor manda ao cliente um .xlsx com a classificação, a idade e a decisão
 * do arquiteto de cada objeto, para a pré-validação. A janela mostra antes de
 * gerar quantos objetos vão, quantos saem sem decisão e as primeiras linhas, do
 * jeito que a aba "WRICEF" do arquivo vai mostrar. O arquivo é montado no
 * servidor, que é quem sabe pintar a planilha.
 *
 * `query` é o filtro atual da lista, inteiro: o escopo "filtro atual" usa todo
 * ele, e os outros usam só a ordem e a varredura.
 */
export function WricefClientReportButton({
  projectId,
  query,
}: {
  projectId: string;
  query: WricefCatalogQuery;
}) {
  const { t } = useTranslation();
  const canSend = hasMinRole(useCurrentUser()?.role, "consultant");
  const [open, setOpen] = useState(false);

  if (!canSend) return null;

  return (
    <>
      <Button
        size="sm"
        className="h-8"
        onClick={() => setOpen(true)}
        title={t("Generate the .xlsx to send to the client for validation")}
      >
        <FileSpreadsheet className="h-3.5 w-3.5 mr-1" />
        {t("Client report")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        {/* Coluna flexível, e não a grade padrão do Dialog: na grade, a tabela
            larga da prévia esticava a janela inteira para o lado, e o bloco
            "Conteúdo" e os botões só apareciam rolando. Assim o título e os
            botões ficam parados, e só o miolo rola. */}
        <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-6xl flex-col overflow-hidden">
          {/* As consultas vivem aqui dentro, montadas só com a janela aberta: a
              barra da lista renderiza sem React Query nos testes da aba. */}
          <ClientReportDialogBody
            projectId={projectId}
            query={query}
            onClose={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

function ClientReportDialogBody({
  projectId,
  query,
  onClose,
}: {
  projectId: string;
  query: WricefCatalogQuery;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [options, setOptions] = useState<WricefClientReportOptions>({
    scope: "all",
    includeClientColumns: true,
    analyzedOnly: false,
  });
  const preview = useWricefClientReportPreview(projectId, query, options);
  const download = useDownloadWricefClientReport(projectId, query, options);
  const data = preview.data;
  const tooLarge = detailCode(preview.error) === "report_too_large";

  return (
    <>
      <DialogHeader>
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
          {t("WRICEF report for client validation")}
        </p>
        <DialogTitle>{t("Generate report (.xlsx)")}</DialogTitle>
        <DialogDescription className="sr-only">
          {t("Choose the objects and the content, check the preview and download the file.")}
        </DialogDescription>
      </DialogHeader>

      <div
        data-testid="client-report-body"
        className="min-h-0 min-w-0 flex-1 space-y-4 overflow-y-auto pr-1"
      >
        <div className="grid gap-3 md:grid-cols-2">
          <ScopeOptions
            value={options.scope}
            counts={data?.scope_counts}
            onChange={(scope) => setOptions((o) => ({ ...o, scope }))}
            analyzedOnly={options.analyzedOnly}
            onAnalyzedOnlyChange={(analyzedOnly) => setOptions((o) => ({ ...o, analyzedOnly }))}
          />
          <fieldset className="min-w-0 rounded-md border px-3 pb-3 pt-1">
            <legend className="px-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              {t("Content")}
            </legend>
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-primary"
                checked={options.includeClientColumns}
                onChange={(e) =>
                  setOptions((o) => ({ ...o, includeClientColumns: e.target.checked }))
                }
              />
              {t("Include columns for the client to answer (Opinion and Comment)")}
            </label>
            <p className="mt-2 text-xs text-muted-foreground">
              {t(
                "\"Resumo\" sheet with totals by WRICEF, decision and module, legend and instructions; \"WRICEF\" sheet with one row per object. Internal technical fields (criticality, Clean Core, effort, confidence, code path) are left out.",
              )}
            </p>
          </fieldset>
        </div>

        {preview.isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : preview.error ? (
          <p role="alert" className="text-sm text-destructive">
            {tooLarge
              ? t("The report would be too large for one file. Narrow it down with the list filters.")
              : t("Could not load the report preview.")}
          </p>
        ) : data ? (
          <ClientReportPreview
            preview={data}
            refreshing={preview.isFetching}
            analyzedOnly={options.analyzedOnly}
          />
        ) : null}
      </div>

      <DialogFooter className="items-center gap-2 sm:justify-between">
        <span className="text-xs text-muted-foreground" data-testid="client-report-filename">
          {data && t("File: {{name}}", { name: data.filename })}
        </span>
        <div className="flex items-center gap-2">
          {download.isError && (
            <span role="alert" className="text-xs text-destructive">
              {t("Could not generate the report. Try again.")}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={onClose}>
            {t("Close")}
          </Button>
          <Button
            size="sm"
            disabled={!data || data.total === 0 || preview.isFetching || download.isPending}
            onClick={() => download.mutate()}
          >
            {download.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
            {t("Generate and download")}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

function ScopeOptions({
  value,
  counts,
  onChange,
  analyzedOnly,
  onAnalyzedOnlyChange,
}: {
  value: WricefClientReportScope;
  counts: Record<string, number> | undefined;
  onChange: (scope: WricefClientReportScope) => void;
  analyzedOnly: boolean;
  onAnalyzedOnlyChange: (analyzedOnly: boolean) => void;
}) {
  const { t } = useTranslation();
  const count = (scope: WricefClientReportScope) => counts?.[scope] ?? "…";
  const choices: { scope: WricefClientReportScope; label: string }[] = [
    { scope: "all", label: t("All catalog objects ({{n}})", { n: count("all") }) },
    {
      scope: "filtered",
      label: t("Only the list's current filter ({{n}})", { n: count("filtered") }),
    },
    { scope: "decided", label: t("Only objects with a decision ({{n}})", { n: count("decided") }) },
    {
      scope: "client_validation",
      label: t("Only \"Pending client validation\" ({{n}})", { n: count("client_validation") }),
    },
  ];
  return (
    <fieldset className="min-w-0 rounded-md border px-3 pb-3 pt-1">
      <legend className="px-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
        {t("Objects in the report")}
      </legend>
      <div className="space-y-1.5">
        {choices.map((choice) => (
          <label key={choice.scope} className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="radio"
              name="client-report-scope"
              className="h-4 w-4 accent-primary"
              value={choice.scope}
              checked={value === choice.scope}
              onChange={() => onChange(choice.scope)}
            />
            {choice.label}
          </label>
        ))}
      </div>
      {/* Cartão 3.5 (Marco, 22/10/2026): vale para qualquer escopo, e os números
          acima passam a contar só os analisados. */}
      <label className="mt-2 flex cursor-pointer items-center gap-2 border-t pt-2 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4 accent-primary"
          checked={analyzedOnly}
          onChange={(e) => onAnalyzedOnlyChange(e.target.checked)}
        />
        {t("Only objects already analyzed by the AI")}
      </label>
    </fieldset>
  );
}

/** Aviso âmbar da janela: o que vai sair diferente no arquivo. */
function WarningNote({ children }: { children: ReactNode }) {
  return (
    <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

function ClientReportPreview({
  preview,
  refreshing,
  analyzedOnly,
}: {
  preview: WricefClientReportPreview;
  refreshing: boolean;
  analyzedOnly: boolean;
}) {
  const { t } = useTranslation();
  const meta = makeDecisionMeta(t);
  const undecided = preview.decision_counts[API_UNDECIDED] ?? 0;
  const pending = preview.pending_analysis;
  const flaggedStandard = preview.flagged_standard;
  const countOf = (token: DecisionOrUndecided) =>
    preview.decision_counts[token === UNDECIDED ? API_UNDECIDED : token] ?? 0;

  return (
    <div
      data-testid="client-report-preview"
      className={cn("space-y-3 text-sm", refreshing && "opacity-60")}
    >
      {pending > 0 && !analyzedOnly && (
        <WarningNote>
          {tp(
            t,
            pending,
            "{{n}} object has not been analyzed by the AI yet and will show \"Análise pendente\" in the description, module and suggested scope item. To leave it out, check \"Only objects already analyzed by the AI\".",
            "{{n}} objects have not been analyzed by the AI yet and will show \"Análise pendente\" in the description, module and suggested scope item. To leave them out, check \"Only objects already analyzed by the AI\".",
            { n: pending },
          )}
        </WarningNote>
      )}
      {pending > 0 && analyzedOnly && (
        <p className="text-xs text-muted-foreground">
          {tp(
            t,
            pending,
            "{{n}} object not yet analyzed by the AI is left out of the file.",
            "{{n}} objects not yet analyzed by the AI are left out of the file.",
            { n: pending },
          )}
        </p>
      )}
      {undecided > 0 && (
        <WarningNote>
          {tp(
            t,
            undecided,
            "{{n}} object without an architect decision will be listed as \"No decision\", and the client can only comment on it. To send only decided objects, choose \"Only objects with a decision\".",
            "{{n}} objects without an architect decision will be listed as \"No decision\", and the client can only comment on them. To send only decided objects, choose \"Only objects with a decision\".",
            { n: undecided },
          )}
        </WarningNote>
      )}
      {/* Cartão 3.2 (Marco, 22/10/2026): avisa sem bloquear o "Gerar e baixar". */}
      {flaggedStandard > 0 && (
        <WarningNote>
          {tp(
            t,
            flaggedStandard,
            "{{n}} object \"Covered by standard\" has a scope item outside the approved scope or not available in Brazil. It is flagged in the file.",
            "{{n}} objects \"Covered by standard\" have a scope item outside the approved scope or not available in Brazil. They are flagged in the file.",
            { n: flaggedStandard },
          )}
        </WarningNote>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        {DECISION_ORDER.map((token) => (
          <span
            key={token}
            data-testid={`client-report-count-${token}`}
            className="inline-flex items-center gap-1.5 rounded border bg-background px-2 py-0.5 text-xs tabular-nums"
          >
            <span
              aria-hidden="true"
              className={cn("h-2.5 w-2.5 shrink-0 rounded-sm", DECISION_BAR[token])}
            />
            {meta[token].label} · {countOf(token)}
          </span>
        ))}
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {tp(t, preview.total, "{{n}} object", "{{n}} objects", { n: preview.total })}
        </span>
      </div>

      {preview.total === 0 ? (
        <p className="text-muted-foreground">{t("No object in this selection.")}</p>
      ) : (
        <div>
          <p className="mb-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            {t("Preview of the first rows of the \"WRICEF\" sheet")}
          </p>
          <div
            data-testid="client-report-table"
            className="max-w-full overflow-x-auto rounded-md border"
          >
            {/* `border-separate`: com as bordas coladas, a célula parada perde a
                linha de cima ao rolar. */}
            <table className="min-w-full border-separate border-spacing-0 text-xs">
              <thead>
                <tr>
                  {preview.columns.map((column) => (
                    <th
                      key={column.key}
                      className={cn(
                        "whitespace-nowrap bg-muted px-2 py-1.5 text-left font-mono text-[10px] font-medium uppercase tracking-wide text-muted-foreground",
                        STICKY_COLUMNS[column.key],
                      )}
                    >
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row, index) => (
                  <tr key={index} className="align-top">
                    {preview.columns.map((column) => (
                      <td
                        key={column.key}
                        className={cn(
                          "border-t px-2 py-1.5",
                          STICKY_COLUMNS[column.key] && cn(STICKY_COLUMNS[column.key], "bg-background"),
                        )}
                      >
                        <PreviewCell columnKey={column.key} row={row} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/** Uma célula da prévia: o texto como vai no arquivo, e a decisão com a cor da tela. */
function PreviewCell({ columnKey, row }: { columnKey: string; row: Record<string, unknown> }) {
  const value = row[columnKey];
  if (columnKey === "decision") {
    const token = ((row.decision_key as string | null) ?? UNDECIDED) as DecisionOrUndecided;
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
        <span
          aria-hidden="true"
          className={cn("h-2.5 w-2.5 shrink-0 rounded-sm", DECISION_BAR[token])}
        />
        {String(value ?? "")}
      </span>
    );
  }
  if (columnKey === "description" || columnKey === "justification") {
    return <span className="line-clamp-3 min-w-[240px]">{String(value ?? "")}</span>;
  }
  const warnings = row.standard_warnings as string[] | undefined;
  if (columnKey === "standard_scope_item" && warnings?.length) {
    // A mesma marca da célula no arquivo: o aviso já vem no texto.
    return (
      <span className="whitespace-nowrap rounded bg-amber-100 px-1 font-medium text-amber-900">
        {String(value ?? "")}
      </span>
    );
  }
  return <span className="whitespace-nowrap">{value == null ? "" : String(value)}</span>;
}
